<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { fetchMarketplaceJson } from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const accountId = route.params.accountId;

const COLUMNS = [
  { key: 'transaction_date', label: 'Tarih' },
  { key: 'channel_label', label: 'Mağaza' },
  { key: 'category_label', label: 'Kategori' },
  { key: 'transaction_type', label: 'Fatura Tipi' },
  { key: 'description', label: 'Açıklama' },
  { key: 'amount', label: 'Tutar' },
];

const filters = reactive({
  channel: 'all',
  category: 'all',
  view: 'additional',
  start: '',
  end: '',
});

const rows = ref([]);
const summary = ref({});
const categories = ref([]);
const pagination = ref({ page: 1, per_page: 100, total: 0, total_pages: 0 });
const meta = ref({});
const loading = ref(false);
const errorMsg = ref('');
const page = ref(1);
const exporting = ref(false);

const queryString = () => {
  const p = new URLSearchParams();
  if (filters.channel !== 'all') p.set('channel', filters.channel);
  if (filters.category !== 'all') p.set('category', filters.category);
  if (filters.view !== 'additional') p.set('view', filters.view);
  if (filters.start) p.set('start', filters.start);
  if (filters.end) p.set('end', filters.end);
  return p;
};

const load = async () => {
  loading.value = true;
  errorMsg.value = '';
  try {
    const p = queryString();
    p.set('page', page.value);
    const data = await fetchMarketplaceJson(
      accountId,
      `trendyol_expenses?${p.toString()}`
    );
    rows.value = data.expenses;
    summary.value = data.summary;
    categories.value = data.categories;
    pagination.value = data.pagination;
    meta.value = data.meta;
  } catch (e) {
    errorMsg.value = 'Veriler yüklenemedi. Lütfen tekrar deneyin.';
  } finally {
    loading.value = false;
  }
};

let debounce = null;
watch(
  () => ({ ...filters }),
  () => {
    clearTimeout(debounce);
    debounce = setTimeout(() => {
      page.value = 1;
      load();
    }, 300);
  }
);

const goToPage = n => {
  if (n < 1 || n > pagination.value.total_pages) return;
  page.value = n;
  load();
};

const resetFilters = () => {
  filters.channel = 'all';
  filters.category = 'all';
  filters.view = 'additional';
  filters.start = '';
  filters.end = '';
};

const moneyFmt = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const money = v => `${moneyFmt.format(Number(v) || 0)} ₺`;
const fmtDate = iso => (iso ? new Date(iso).toLocaleDateString('tr-TR') : '—');

const CAT_COLORS = {
  reklam: 'text-purple-700 dark:text-purple-400',
  ceza: 'text-ruby-700 dark:text-ruby-400',
  uluslararasi: 'text-blue-700 dark:text-blue-400',
  stopaj: 'text-amber-700 dark:text-amber-400',
  diger: 'text-n-slate-11',
};

const additionalCats = computed(() =>
  (summary.value.by_category || []).filter(c => !c.already_counted)
);
const countedCats = computed(() =>
  (summary.value.by_category || []).filter(c => c.already_counted)
);

const triggerDownload = (content, filename, mime) => {
  const blob = new Blob(['﻿', content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

const fetchAll = async () => {
  const data = await fetchMarketplaceJson(
    accountId,
    `trendyol_expenses/export?${queryString().toString()}`
  );
  return data.expenses || [];
};

const exportCsv = async () => {
  exporting.value = true;
  try {
    const data = await fetchAll();
    const head = COLUMNS.map(c => c.label).join(';');
    const lines = data.map(r =>
      [
        fmtDate(r.transaction_date),
        r.channel_label,
        r.category_label,
        r.transaction_type,
        r.description || '',
        r.amount,
      ]
        .map(v => `"${String(v ?? '').replace(/"/g, '""')}"`)
        .join(';')
    );
    triggerDownload(
      [head, ...lines].join('\r\n'),
      `trendyol-giderler-${new Date().toISOString().slice(0, 10)}.csv`,
      'text/csv;charset=utf-8;'
    );
  } finally {
    exporting.value = false;
  }
};

const exportExcel = async () => {
  exporting.value = true;
  try {
    const data = await fetchAll();
    const esc = s =>
      String(s ?? '').replace(
        /[&<>]/g,
        c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c]
      );
    const head = COLUMNS.map(c => `<th>${esc(c.label)}</th>`).join('');
    const body = data
      .map(
        r =>
          `<tr><td>${fmtDate(r.transaction_date)}</td><td>${esc(r.channel_label)}</td><td>${esc(r.category_label)}</td><td>${esc(r.transaction_type)}</td><td>${esc(r.description || '')}</td><td>${r.amount}</td></tr>`
      )
      .join('');
    triggerDownload(
      `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body><table border="1"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></body></html>`,
      `trendyol-giderler-${new Date().toISOString().slice(0, 10)}.xls`,
      'application/vnd.ms-excel;charset=utf-8;'
    );
  } finally {
    exporting.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full">
    <div class="flex flex-col gap-4 pt-1">
      <!-- Açıklama -->
      <div class="flex flex-col gap-1">
        <p class="text-sm text-n-slate-11">
          Trendyol'un sana <strong>fatura ettiği</strong>, siparişe bağlanamayan
          dönem-bazlı giderler (reklam, ceza, uluslararası hizmet, stopaj).
          Komisyon/Kargo/Platform faturaları sipariş bazında zaten düşüldüğü
          için <strong>çift sayılmaz</strong> (soluk gösterilir).
        </p>
      </div>

      <!-- Ek gider toplamı + kategori kartları -->
      <div class="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <div
          class="flex flex-col gap-0.5 p-3 rounded-xl bg-n-solid-2 border border-ruby-300 dark:border-ruby-800 ring-1 ring-ruby-400"
        >
          <span class="text-xs font-medium text-n-slate-11"
            >EK GİDER TOPLAMI</span
          >
          <span
            class="text-xl font-semibold tabular-nums text-ruby-700 dark:text-ruby-400"
          >
            {{ money(summary.additional_total) }}
          </span>
          <span class="text-[11px] text-n-slate-10"
            >{{ summary.additional_count || 0 }} fatura · sipariş net'i
            dışında</span
          >
        </div>
        <div
          v-for="c in additionalCats"
          :key="c.category"
          class="flex flex-col gap-0.5 p-3 rounded-xl bg-n-solid-2 border border-n-weak"
        >
          <span class="text-xs font-medium text-n-slate-11">{{ c.label }}</span>
          <span
            class="text-lg font-semibold tabular-nums"
            :class="CAT_COLORS[c.category] || 'text-n-slate-12'"
          >
            {{ money(c.total) }}
          </span>
          <span class="text-[11px] text-n-slate-10">{{ c.count }} fatura</span>
        </div>
      </div>

      <!-- Çift-sayma referansı -->
      <div
        v-if="countedCats.length"
        class="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-2 rounded-lg bg-n-alpha-black1 border border-n-weak"
      >
        <span class="text-xs font-medium text-n-slate-10"
          >Sipariş bazında zaten düşülenler (referans, tekrar sayılmaz):</span
        >
        <span
          v-for="c in countedCats"
          :key="c.category"
          class="text-xs text-n-slate-11"
        >
          {{ c.label }}:
          <span class="tabular-nums text-n-slate-12">{{ money(c.total) }}</span>
        </span>
      </div>

      <!-- Filtreler -->
      <div
        class="grid grid-cols-2 gap-3 p-4 rounded-xl md:grid-cols-3 lg:grid-cols-6 bg-n-solid-2 border border-n-weak"
      >
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">Görünüm</label>
          <select
            v-model="filters.view"
            class="px-2 py-1.5 text-sm rounded-lg bg-n-alpha-black2 border border-n-weak text-n-slate-12"
          >
            <option value="additional">Ek Giderler</option>
            <option value="counted">Zaten Sayılanlar</option>
            <option value="all">Tümü</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">Kategori</label>
          <select
            v-model="filters.category"
            class="px-2 py-1.5 text-sm rounded-lg bg-n-alpha-black2 border border-n-weak text-n-slate-12"
          >
            <option value="all">Tümü</option>
            <option v-for="c in categories" :key="c.value" :value="c.value">
              {{ c.label }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">Mağaza</label>
          <select
            v-model="filters.channel"
            class="px-2 py-1.5 text-sm rounded-lg bg-n-alpha-black2 border border-n-weak text-n-slate-12"
          >
            <option value="all">Tümü</option>
            <option value="trendyol_koza">Trendyol · Koza</option>
            <option value="trendyol_modax">Trendyol · ModaX</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">Başlangıç</label>
          <input
            v-model="filters.start"
            type="date"
            class="px-2 py-1.5 text-sm rounded-lg bg-n-alpha-black2 border border-n-weak text-n-slate-12"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">Bitiş</label>
          <input
            v-model="filters.end"
            type="date"
            class="px-2 py-1.5 text-sm rounded-lg bg-n-alpha-black2 border border-n-weak text-n-slate-12"
          />
        </div>
        <div class="flex items-end">
          <button
            class="px-3 py-1.5 text-sm rounded-lg bg-n-alpha-black2 border border-n-weak text-n-slate-11 hover:text-n-slate-12 w-full"
            @click="resetFilters"
          >
            Temizle
          </button>
        </div>
      </div>

      <!-- Export + sayı -->
      <div class="flex flex-wrap items-center justify-between gap-2">
        <span class="text-sm text-n-slate-11"
          >{{ pagination.total }} fatura kaydı</span
        >
        <div class="flex gap-2">
          <button
            :disabled="exporting"
            class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
            @click="exportExcel"
          >
            <span class="i-lucide-file-spreadsheet" /> Excel
          </button>
          <button
            :disabled="exporting"
            class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-n-alpha-black2 border border-n-weak text-n-slate-12 disabled:opacity-50"
            @click="exportCsv"
          >
            <span class="i-lucide-download" /> CSV
          </button>
        </div>
      </div>

      <p v-if="errorMsg" class="text-sm text-ruby-700 dark:text-ruby-400">
        {{ errorMsg }}
      </p>

      <!-- Tablo -->
      <div class="overflow-x-auto rounded-xl border border-n-weak">
        <table class="w-full text-sm border-collapse min-w-[900px]">
          <thead>
            <tr class="bg-n-solid-2 text-n-slate-11">
              <th
                v-for="col in COLUMNS"
                :key="col.key"
                class="px-3 py-2 font-medium text-left whitespace-nowrap"
                :class="col.key === 'amount' ? 'text-right' : ''"
              >
                {{ col.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td
                :colspan="COLUMNS.length"
                class="px-3 py-8 text-center text-n-slate-10"
              >
                Yükleniyor...
              </td>
            </tr>
            <tr v-else-if="!rows.length">
              <td
                :colspan="COLUMNS.length"
                class="px-3 py-8 text-center text-n-slate-10"
              >
                Kayıt bulunamadı.
              </td>
            </tr>
            <tr
              v-for="r in rows"
              v-else
              :key="r.id"
              class="border-t border-n-weak"
              :class="r.already_counted ? 'opacity-60' : ''"
            >
              <td class="px-3 py-2 whitespace-nowrap text-n-slate-12">
                {{ fmtDate(r.transaction_date) }}
              </td>
              <td class="px-3 py-2 text-n-slate-11">{{ r.channel_label }}</td>
              <td class="px-3 py-2">
                <span
                  class="text-xs font-medium"
                  :class="CAT_COLORS[r.category] || 'text-n-slate-11'"
                >
                  {{ r.category_label }}
                </span>
              </td>
              <td class="px-3 py-2 text-n-slate-11">
                {{ r.transaction_type }}
              </td>
              <td
                class="px-3 py-2 text-n-slate-10 max-w-md truncate"
                :title="r.description"
              >
                {{ r.description || '—' }}
              </td>
              <td
                class="px-3 py-2 text-right tabular-nums"
                :class="
                  r.amount < 0
                    ? 'text-green-700 dark:text-green-400'
                    : 'text-ruby-700 dark:text-ruby-400'
                "
              >
                {{ money(-r.amount) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Sayfalama -->
      <div
        v-if="pagination.total_pages > 1"
        class="flex items-center justify-center gap-2 py-2"
      >
        <button
          class="px-3 py-1.5 text-sm rounded-lg bg-n-solid-2 border border-n-weak text-n-slate-12 disabled:opacity-40"
          :disabled="pagination.page <= 1"
          @click="goToPage(pagination.page - 1)"
        >
          Önceki
        </button>
        <span class="text-sm text-n-slate-11">
          Sayfa {{ pagination.page }} / {{ pagination.total_pages }}
        </span>
        <button
          class="px-3 py-1.5 text-sm rounded-lg bg-n-solid-2 border border-n-weak text-n-slate-12 disabled:opacity-40"
          :disabled="pagination.page >= pagination.total_pages"
          @click="goToPage(pagination.page + 1)"
        >
          Sonraki
        </button>
      </div>
    </div>
  </div>
</template>
